import { useState } from "react";
import { X, Zap, Shield, Sparkles, Users, Mic, Globe } from "lucide-react";
import { cn } from "@/lib/utils";

const HIGHLIGHTS = [
  { icon: Zap, title: "All 6 Guides", desc: "Friend, Mentor, Social Guide, Job Guardian, Life Coach, Storyteller — all unlocked." },
  { icon: Shield, title: "Unlimited Messages", desc: "No daily caps. Test, explore, and push Omni as hard as you need." },
  { icon: Mic, title: "Voice Interaction", desc: "Speak to Omni with TTS and STT. Say \"Hey Omni\" to activate hands-free." },
  { icon: Globe, title: "33 Languages", desc: "Switch languages anytime in Settings. Omni adapts instantly." },
  { icon: Users, title: "Family Dashboard", desc: "Create guardian groups, invite codes, and track emotional wellbeing." },
  { icon: Sparkles, title: "Bulk Mode & Shortcuts", desc: "Ctrl+B for bulk questions, Ctrl+N new chat, Ctrl+K search, Ctrl+D dark mode." },
];

export function TeamOnboarding({ onComplete }) {
  const [dontShow, setDontShow] = useState(false);
  const [step, setStep] = useState(0);

  const handleFinish = () => {
    if (dontShow) {
      localStorage.setItem("omni_team_onboarding", "permanent");
    } else {
      localStorage.setItem("omni_team_onboarding", "done");
    }
    onComplete();
  };

  const current = HIGHLIGHTS[step];
  const Icon = current.icon;
  const isLast = step === HIGHLIGHTS.length - 1;

  return (
    <div
      data-testid="team-onboarding-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div className="bg-card border border-border/50 rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-in zoom-in-95 slide-in-from-bottom-3 duration-250">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-2">
          <button
            data-testid="team-onboarding-close"
            onClick={handleFinish}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-muted/60 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Welcome to Omni</h3>
              <p className="text-[11px] text-muted-foreground">Your all-access companion</p>
            </div>
          </div>
        </div>

        {/* Card content */}
        <div className="px-6 py-5">
          <div className="bg-muted/30 rounded-xl p-5 mb-4 border border-border/30">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-1">{current.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{current.desc}</p>
              </div>
            </div>
          </div>

          {/* Step dots */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {HIGHLIGHTS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  i === step ? "bg-primary w-5" : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                )}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border/30 px-6 py-4 flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              data-testid="team-onboarding-dont-show"
              type="checkbox"
              checked={dontShow}
              onChange={e => setDontShow(e.target.checked)}
              className="w-4 h-4 rounded border-border accent-primary"
            />
            <span className="text-xs text-muted-foreground">Don't show again</span>
          </label>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="px-3 py-1.5 rounded-xl text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
              >
                Back
              </button>
            )}
            <button
              data-testid={isLast ? "team-onboarding-finish" : "team-onboarding-next"}
              onClick={isLast ? handleFinish : () => setStep(s => s + 1)}
              className="px-4 py-1.5 rounded-xl text-xs font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              {isLast ? "Let's Go" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
